import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Employee, EmployeeStatus, Department } from '../../types';
import { employeesService, EmployeeFilters } from '../../services/api/employees';
import { departmentsService } from '../../services/api/departments';
import Loading from '../../components/common/Loading';
import ErrorMessage from '../../components/common/ErrorMessage';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import EmployeeFormModal from './EmployeeFormModal';
import EmployeeImportModal from './EmployeeImportModal';
import { useToast } from '../../contexts/ToastContext';
import './EmployeeList.css';

const EmployeeList: React.FC = () => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useToast();

  // Data state
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  // Modals
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; employee: Employee | null }>({
    isOpen: false,
    employee: null,
  });
  const [deleting, setDeleting] = useState(false);

  // Debounce search
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchEmployees = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filters: EmployeeFilters = {
        page,
        pageSize,
        q: debouncedSearch || undefined,
        departmentId: departmentFilter ? Number(departmentFilter) : undefined,
        status: statusFilter as EmployeeStatus || undefined,
      };
      const response = await employeesService.getAll(filters);
      setEmployees(response.items);
      setTotal(response.total);
    } catch (err: any) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, debouncedSearch, departmentFilter, statusFilter]);

  const fetchDepartments = useCallback(async () => {
    try {
      const depts = await departmentsService.getAll();
      setDepartments(depts);
    } catch (err) {
      console.error('Failed to load departments:', err);
    }
  }, []);

  useEffect(() => {
    fetchDepartments();
  }, [fetchDepartments]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  const handleCreate = () => {
    setEditingEmployee(null);
    setIsFormModalOpen(true);
  };

  const handleEdit = (employee: Employee) => {
    setEditingEmployee(employee);
    setIsFormModalOpen(true);
  };

  const handleDelete = (employee: Employee) => {
    setDeleteConfirm({ isOpen: true, employee });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm.employee) return;

    setDeleting(true);
    try {
      await employeesService.delete(deleteConfirm.employee.id);
      showSuccess('Employee deleted successfully');
      fetchEmployees();
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Delete failed';
      showError(errorMessage);
    } finally {
      setDeleting(false);
      setDeleteConfirm({ isOpen: false, employee: null });
    }
  };

  const getDepartmentName = (departmentId: number) => {
    const dept = departments.find((d) => d.id === departmentId);
    return dept ? dept.name : `ID: ${departmentId}`;
  };

  const totalPages = Math.ceil(total / pageSize);

  if (error && employees.length === 0) {
    return <ErrorMessage error={error} onRetry={fetchEmployees} />;
  }

  return (
    <div className="employee-list-page">
      <div className="page-header">
        <h1>{t('employees.title')}</h1>
        <div className="header-actions">
          <button className="button button-secondary" onClick={() => setIsImportModalOpen(true)}>
            📥 {t('employees.import', 'Import')}
          </button>
          <button className="button button-primary" onClick={handleCreate}>
            + {t('employees.create')}
          </button>
        </div>
      </div>

      <div className="filters-bar">
        <input
          type="text"
          className="search-input"
          placeholder={t('common.search') + '...'}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <select
          className="filter-select"
          value={departmentFilter}
          onChange={(e) => {
            setDepartmentFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="">{t('reports.filters.allDepartments')}</option>
          {departments.map((dept) => (
            <option key={dept.id} value={dept.id}>
              {dept.name}
            </option>
          ))}
        </select>
        <select
          className="filter-select"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="">{t('common.status')}: All</option>
          <option value="ACTIVE">{t('status.active')}</option>
          <option value="INACTIVE">{t('status.inactive')}</option>
        </select>
        <span className="results-count">
          {total} {t('employees.title').toLowerCase()}
        </span>
      </div>

      {loading && employees.length === 0 ? (
        <Loading text={t('common.loading')} />
      ) : employees.length === 0 ? (
        <div className="empty-state">
          <p>{t('common.noData')}</p>
          <button className="button button-primary" onClick={handleCreate}>
            {t('employees.create')}
          </button>
        </div>
      ) : (
        <>
          <div className="employees-table">
            <table>
              <thead>
                <tr>
                  <th>{t('employees.employeeCode')}</th>
                  <th>{t('employees.fullName')}</th>
                  <th>{t('employees.email')}</th>
                  <th>{t('employees.department')}</th>
                  <th>{t('employees.joinDate')}</th>
                  <th>{t('employees.status')}</th>
                  <th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {employees.map((employee) => (
                  <tr key={employee.id}>
                    <td>{employee.employeeCode}</td>
                    <td>{employee.fullName}</td>
                    <td>{employee.email}</td>
                    <td>{getDepartmentName(employee.departmentId)}</td>
                    <td>{new Date(employee.joinDate).toLocaleDateString('en-GB')}</td>
                    <td>
                      <span
                        className={`status-badge ${employee.status.toLowerCase()}`}
                      >
                        {t(`status.${employee.status.toLowerCase()}`)}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="button-link"
                          onClick={() => handleEdit(employee)}
                        >
                          {t('common.edit')}
                        </button>
                        <button
                          className="button-link danger"
                          onClick={() => handleDelete(employee)}
                        >
                          {t('common.delete')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="button button-secondary button-small"
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
              >
                Previous
              </button>
              <span className="page-info">
                Page {page} of {totalPages}
              </span>
              <button
                className="button button-secondary button-small"
                disabled={page >= totalPages}
                onClick={() => setPage(page + 1)}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* Form Modal */}
      <EmployeeFormModal
        isOpen={isFormModalOpen}
        onClose={() => {
          setIsFormModalOpen(false);
          setEditingEmployee(null);
        }}
        onSuccess={fetchEmployees}
        employee={editingEmployee}
      />

      {/* Import Modal */}
      <EmployeeImportModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onSuccess={fetchEmployees}
      />

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, employee: null })}
        onConfirm={confirmDelete}
        title={t('common.delete') + ' ' + t('employees.title')}
        message={`Are you sure you want to delete employee "${deleteConfirm.employee?.fullName}"? This will set their status to INACTIVE.`}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        variant="danger"
        isLoading={deleting}
      />
    </div>
  );
};

export default EmployeeList;
